import { useState } from 'react';

import styled from '@emotion/styled';
import {
  Button,
  Colors,
  Flex,
  IconUploadLine,
  Text,
  PrimaryColors,
  CloseLine,
  IconCheckSmallLine,
} from '@revenuemarket/cds';
import { Toast } from '@revenuemarket/ui';
import forge from 'node-forge';
import { extractP12Certificate } from '~utils/certificate/pfx';
import { getFilesFromLocal } from '~utils/file/getFilesFromLocal';
import { validatePfxFile } from '~utils/file/validator';

const Wrapper = styled(Flex)<{ isDragOver: boolean }>`
  border-radius: 16px;
  border: 1px solid
    ${({ isDragOver }) => (isDragOver ? PrimaryColors.primary500 : Colors.grayOpacity04)};
  background: ${({ isDragOver }) => (isDragOver ? PrimaryColors.primary50 : Colors.gray30)};
  padding: 24px 16px;
  transition: all 0.2s ease-in-out;
`;

const FindCertSection = ({
  certificate,
  setCertificate,
}: {
  certificate?: { encoded: string; extracted?: string; fileName?: string };
  setCertificate: (certificate?: {
    encoded: string;
    extracted?: string;
    fileName?: string;
  }) => void;
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const processCertificateFile = async (file: File) => {
    if (!(await validatePfxFile(file))) {
      Toast.error('이 파일 양식은 등록할 수 없어요');
      return;
    }

    const buffer = await file.arrayBuffer();
    const encoded = forge.util.binary.base64.encode(new Uint8Array(buffer));

    try {
      const certificate = extractP12Certificate(encoded);
      setCertificate({ encoded, extracted: certificate, fileName: file.name });
    } catch {
      setCertificate({ encoded, fileName: file.name });
    }
  };

  const uploadCertificate = async () => {
    const files = await getFilesFromLocal({
      accept: ['application/x-pkcs12'],
      multiple: false,
    });

    if (files != null) {
      const [file] = files;
      await processCertificateFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      await processCertificateFile(file);
    }
  };

  return (
    <Wrapper
      direction="column"
      gap={8}
      align="center"
      justify="center"
      isDragOver={isDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ minHeight: !certificate ? 186 : undefined }}
    >
      {!certificate ? (
        <>
          <IconUploadLine
            width={40}
            height={40}
            fill={isDragOver ? PrimaryColors.primary500 : Colors.gray900}
          />
          <Text
            textStyle="14_500"
            style={{ color: isDragOver ? PrimaryColors.primary600 : Colors.gray800 }}
          >
            .pfx 파일을 이곳으로 끌어다 놓기
          </Text>
          {!isDragOver && (
            <>
              <Text textStyle="14_500" style={{ color: Colors.gray500 }}>
                또는
              </Text>
              <Button buttonType="primary" onClick={uploadCertificate}>
                .pfx 파일찾기
              </Button>
            </>
          )}
        </>
      ) : (
        <Flex justify="space-between" gap={8} align="center">
          <IconCheckSmallLine width={32} height={32} fill={PrimaryColors.primary600} />
          <Text textStyle="14_500" style={{ color: Colors.gray900 }}>
            {certificate.fileName}
          </Text>
          <Button
            buttonType="transparent"
            icon={<CloseLine fill={Colors.gray700} width={20} height={20} />}
            onClick={() => setCertificate(undefined)}
          />
        </Flex>
      )}
    </Wrapper>
  );
};

export default FindCertSection;
